import { Component, Input, Output, EventEmitter } from '@angular/core';

export interface FacilityInfo {
  name: string;
  patientId: string;
}

export interface FacilitatorInfo {
  email: string;
  name?: string;
}

@Component({
    selector: 'app-transfer-visualization',
    templateUrl: './transfer-visualization.component.html',
    styleUrls: ['./transfer-visualization.component.scss'],
    standalone: false
})
export class TransferVisualizationComponent {
  @Input() sourceFacility?: FacilityInfo;
  @Input() destinationFacility?: FacilityInfo;
  @Input() facilitator?: FacilitatorInfo; // Backward compatibility - single facilitator
  @Input() facilitators: FacilitatorInfo[] = []; // New - array of facilitators
  @Input() exchangeMode: 'send' | 'receive' | null = null;
  @Input() allowEdit: boolean = true; // Allow editing by default

  @Output() editDestinationFacility = new EventEmitter<void>();
  @Output() editFacilitators = new EventEmitter<void>();

  /**
   * Get all facilitators as an array, combining both the old single facilitator
   * and the new facilitators array for backward compatibility
   */
  get allFacilitators(): FacilitatorInfo[] {
    const result: FacilitatorInfo[] = [...this.facilitators];
    // If old single facilitator is provided and not already in the array, add it
    if (this.facilitator && !result.some(f => f.email === this.facilitator?.email)) {
      result.push(this.facilitator);
    }
    return result;
  }

  formatFacilityName(name: string): string {
    return name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-');
  }

  shouldShow(): boolean {
    // Show when we have source and destination facilities
    // Facilitator is optional - can be shown later when selected
    if (this.sourceFacility && this.destinationFacility) {
      // If exchangeMode is set, show accordingly
      if (this.exchangeMode === 'send') {
        // For send mode, facilitator is required to show facilitator info
        // But we can still show the facility pair
        return true;
      }
      if (this.exchangeMode === 'receive') {
        // For receive mode, show the visualization without facilitator
        return true;
      }
      // If no exchangeMode set yet, still show the facility pair
      return true;
    }
    return false;
  }

  onEditDestinationFacility(): void {
    if (this.allowEdit) {
      this.editDestinationFacility.emit();
    }
  }

  onEditFacilitators(): void {
    if (this.allowEdit) {
      this.editFacilitators.emit();
    }
  }
}

